import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-E: dual-write league match messages to both legacy
// match_chat_messages and new chat_messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, CalendarDays, ShieldAlert } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import RescheduleRequest from "./RescheduleRequest";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import RescheduleCard from "./RescheduleCard";

interface MatchChatProps {
  matchId: string;
}

export default function MatchChat({ matchId }: MatchChatProps) {
  // PR-AV: useTranslation must be called at component scope (TS2304 fix).
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["match-chat", matchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("match_chat_messages")
        .select("*, sender:user_id(full_name)")
        .eq("match_id", matchId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch reschedule requests
  const { data: rescheduleRequests = [] } = useQuery({
    queryKey: ["reschedule-requests", matchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reschedule_requests" as any)
        .select("*")
        .eq("match_id", matchId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Record<string, unknown>[];
    },
  });

  // Realtime subscription for messages
  useEffect(() => {
    const channel = supabase
      .channel(`match-chat-${matchId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "match_chat_messages",
          filter: `match_id=eq.${matchId}`,
        },
        async (payload) => {
          let senderName: string | null = null;
          if (payload.new.user_id) {
            const { data } = await supabase
              .from("public_profiles" as any)
              .select("full_name")
              .eq("id", payload.new.user_id)
              .single();
            senderName = data?.full_name || null;
          }
          const newMsg: Record<string, unknown> = {
            ...payload.new,
            sender: { full_name: senderName },
          };
          queryClient.setQueryData(["match-chat", matchId], (old: Record<string, unknown>[]) => {
            if (!old) return [newMsg];
            if (old.some((m: Record<string, unknown>) => m.id === newMsg.id)) return old;
            return [...old, newMsg];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [matchId, queryClient]);

  // Realtime for reschedule requests
  useEffect(() => {
    const channel = supabase
      .channel(`reschedule-${matchId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "reschedule_requests",
          filter: `match_id=eq.${matchId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["reschedule-requests", matchId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [matchId, queryClient]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // UPPDRAG-8 PR-E: dual-write to legacy match_chat_messages + new chat_messages.
  const sendMutation = useMutation({
    mutationFn: async (text: string) => {
      await sendLeagueMatchMessage(supabase, {
        matchId,
        senderUserId: user!.id,
        body: text,
      });
    },
    onSuccess: () => {
      setMessage("");
    },
  });

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    sendMutation.mutate(trimmed);
  };

  // Find the latest pending reschedule request to show as a card
  const latestPending = rescheduleRequests.find((r: Record<string, unknown>) => r.status === "pending");

  return (
    <div className="flex flex-col h-[50vh]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {isLoading && (
          <p className="text-center text-sm text-muted-foreground">Laddar...</p>
        )}
        {!isLoading && messages.length === 0 && !latestPending && (
          <p className="text-center text-sm text-muted-foreground py-8">
            Inga meddelanden ännu. Skriv något!
          </p>
        )}
        {messages.map((msg: Record<string, unknown>) => {
          if (msg.is_system) {
            // Check if this is a reschedule message and render card
            const isRescheduleMsg = msg.message?.includes("Ombokningsförslag");
            if (isRescheduleMsg && latestPending) {
              return (
                <div key={msg.id} className="space-y-1">
                  <p className="text-[10px] text-muted-foreground/60 text-center">
                    {format(new Date(msg.created_at), "d MMM HH:mm", { locale: sv })}
                  </p>
                  <RescheduleCard request={latestPending} matchId={matchId} />
                </div>
              );
            }

            return (
              <div key={msg.id} className="text-center">
                <p className="text-xs text-muted-foreground italic">
                  {msg.message}
                </p>
                <p className="text-[10px] text-muted-foreground/60">
                  {format(new Date(msg.created_at), "d MMM HH:mm", { locale: sv })}
                </p>
              </div>
            );
          }

          const isOwn = msg.user_id === user?.id;
          return (
            <div
              key={msg.id}
              className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}
            >
              {!isOwn && (
                <p className="text-[10px] text-muted-foreground ml-1 mb-0.5">
                  {msg.sender?.full_name || "Okänd"}
                </p>
              )}
              <div
                className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                  isOwn
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {msg.message}
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5 mx-1">
                {format(new Date(msg.created_at), "HH:mm", { locale: sv })}
              </p>
            </div>
          );
        })}
      </div>

      {showReschedule && (
        <div className="border-t p-2">
          <RescheduleRequest matchId={matchId} onClose={() => setShowReschedule(false)} />
        </div>
      )}

      <div className="border-t p-2 flex gap-2">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setShowReschedule(!showReschedule)}
          title="Föreslå ny tid"
        >
          <CalendarDays className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={async () => {
            if (!user) return;
            // UPPDRAG-8 PR-E: dual-write admin-callout system message.
            // data jsonb carries structured metadata so future renderers
            // can replace the emoji-prefixed text with a proper card.
            await sendLeagueMatchMessage(supabase, {
              matchId,
              senderUserId: user.id,
              body: "🔔 Admin har tillkallats för hjälp med denna match",
              isSystem: true,
              data: { type: "admin_callout", caller_user_id: user.id },
            });
            try {
              if (organization) {
                const { data: adminRoles } = await supabase
                  .from("user_roles")
                  .select("user_id")
                  .eq("role", "admin")
                  .eq("organization_id", organization.id);
                if (adminRoles && adminRoles.length > 0) {
                  await supabase.from("notifications").insert(
                    adminRoles.map((r) => ({
                      user_id: r.user_id,
                      organization_id: organization.id,  // PR-B: org-scope per Phase 1 RLS (gated by `if (organization)` above)
                      title: "Spelare begär hjälp",
                      body: "En seriematch behöver admin-assistans. Öppna matchens chatt.",
                      type: "match_admin_callout",
                      data: { match_id: matchId },
                    }))
                  );
                }
              }
            } catch { /* non-critical */ }
            queryClient.invalidateQueries({ queryKey: ["match-chat", matchId] });
            toast.success(t("activities:leagues.adminCalled"));
          }}
          title="Tillkalla admin"
          className="text-amber-600"
        >
          <ShieldAlert className="h-4 w-4" />
        </Button>
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Skriv ett meddelande..."
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
          disabled={sendMutation.isPending}
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={!message.trim() || sendMutation.isPending}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
